import { Text, TextSize } from '@vizit/atoms';
import { getMetricLabel } from '@vizit/constants';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { BaseMetricCard } from '../base-metric-card';
import { AreaChart } from '../metric-charts/area-chart';
import type { AreaMetric } from '../metric-charts/types';
import { getScoreColorClass } from './utils';

interface AreaMetricCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * The area metric data to display
   */
  metric: AreaMetric;
  /**
   * Optional description text to display below the title
   */
  description?: string;
  /**
   * Width of the chart
   * @default 300
   */
  chartWidth?: number;
  /**
   * Height of the chart
   * @default 60
   */
  chartHeight?: number;
  /**
   * Number of filled zaps for impact badge (0-4)
   */
  filledZapCount: number;
  /**
   * Whether the card is in loading state
   * @default false
   */
  isLoading?: boolean;
}

/**
 * AreaMetricCard displays an area metric with score, chart, and impact badge
 */
const AreaMetricCard = React.forwardRef<HTMLDivElement, AreaMetricCardProps>((props, forwardedRef) => {
  const {
    metric,
    description,
    chartWidth = 300,
    chartHeight = 60, // score-threshold-allow: chart height in px
    filledZapCount,
    isLoading = false,
    ...other
  } = props;

  const [isHovered, setIsHovered] = React.useState(false);
  const label = getMetricLabel(metric.key);
  const scoreColorClass = getScoreColorClass(metric.currentValue);

  return (
    <BaseMetricCard
      ref={forwardedRef}
      label={label}
      description={description}
      filledZapCount={filledZapCount}
      isLoading={isLoading}
      additionalHeaderContent={
        <Text size={TextSize.Xl} className={scoreColorClass} showEllipsis={false}>
          {metric.currentValue}
        </Text>
      }
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      footer={
        <div
          className={cx(
            'flex justify-between items-center text-xs text-gray-utility-500 h-[1.2em]',
            isHovered ? 'opacity-100' : 'opacity-0',
          )}
        >
          <span>0</span>
          {/* score-threshold-allow: chart axis label */}
          <span>100</span>
        </div>
      }
      {...other}
    >
      <AreaChart metric={metric} width={chartWidth} height={chartHeight} />
    </BaseMetricCard>
  );
});

AreaMetricCard.displayName = 'AreaMetricCard';

export type { AreaMetricCardProps };
export { AreaMetricCard };
